/**
 * 斗兽棋游戏逻辑
 * 9×7棋盘，8种动物，通过吃子推进，占领对方巢穴或吃光对方棋子获胜
 */

import { Player, Position, GameMode, Difficulty, JungleAnimal, AnimalType, JungleTerrain, TerrainType, JungleMove, JungleGameState, ANIMAL_RANKS, ANIMAL_NAMES } from '../types/game';

// ==================== 基础工具函数 ====================

/**
 * 检查位置是否在棋盘内
 */
export const isValidPosition = (position: Position): boolean => {
  return position.row >= 0 && position.row < 9 && position.col >= 0 && position.col < 7;
};

/**
 * 获取相邻的格子位置
 */
export const getAdjacentPositions = (position: Position): Position[] => {
  const directions = [
    { row: -1, col: 0 },  // 上
    { row: 1, col: 0 },   // 下
    { row: 0, col: -1 },  // 左
    { row: 0, col: 1 },   // 右
  ];

  return directions
    .map(dir => ({ row: position.row + dir.row, col: position.col + dir.col }))
    .filter(isValidPosition);
};

// ==================== 棋盘和地形设置 ====================

/**
 * 创建初始斗兽棋棋盘 (7×9)
 */
export const createJungleBoard = (): (JungleAnimal | null)[][] => {
  const board: (JungleAnimal | null)[][] = Array(9).fill(null).map(() => Array(7).fill(null));

  // 放置黑方动物（上方）
  // 第一行（避开巢穴和陷阱）：狮、虎
  board[0][0] = { player: 'black', type: 'lion', rank: 7 };
  board[0][6] = { player: 'black', type: 'tiger', rank: 6 };

  // 第二行（避开陷阱）：狗、猫
  board[1][1] = { player: 'black', type: 'dog', rank: 3 };
  board[1][5] = { player: 'black', type: 'cat', rank: 2 };

  // 第三行：鼠、豹、狼、象（交换老鼠和大象位置）
  board[2][0] = { player: 'black', type: 'mouse', rank: 1 };
  board[2][2] = { player: 'black', type: 'leopard', rank: 5 };
  board[2][4] = { player: 'black', type: 'wolf', rank: 4 };
  board[2][6] = { player: 'black', type: 'elephant', rank: 8 };

  // 放置白方动物（下方）
  // 第六行：象、狼、豹、鼠（交换老鼠和大象位置）
  board[6][0] = { player: 'white', type: 'elephant', rank: 8 };
  board[6][2] = { player: 'white', type: 'wolf', rank: 4 };
  board[6][4] = { player: 'white', type: 'leopard', rank: 5 };
  board[6][6] = { player: 'white', type: 'mouse', rank: 1 };

  // 第七行（避开陷阱）：猫、狗
  board[7][1] = { player: 'white', type: 'cat', rank: 2 };
  board[7][5] = { player: 'white', type: 'dog', rank: 3 };

  // 第八行（避开巢穴和陷阱）：虎、狮
  board[8][0] = { player: 'white', type: 'tiger', rank: 6 };
  board[8][6] = { player: 'white', type: 'lion', rank: 7 };

  return board;
};

/**
 * 创建地形
 */
export const createTerrain = (): JungleTerrain[][] => {
  const terrain: JungleTerrain[][] = Array(9).fill(null).map(() =>
    Array(7).fill(null).map(() => ({ type: 'normal' as TerrainType }))
  );

  // 设置河流（标准斗兽棋：两块独立的横向河流）
  const riverPositions: Position[] = [];

  // 横向河流（中间两块区域）
  for (let row = 3; row <= 5; row++) {
    for (let col = 1; col <= 2; col++) {
      terrain[row][col] = { type: 'river' };
      riverPositions.push({ row, col });
    }
    for (let col = 4; col <= 5; col++) {
      terrain[row][col] = { type: 'river' };
      riverPositions.push({ row, col });
    }
  }

  return terrain;
};

/**
 * 设置陷阱和巢穴
 */
export const setupSpecialTerrain = (terrain: JungleTerrain[][]) => {
  // 黑方陷阱（围绕黑方巢穴）
  const blackTraps: Position[] = [
    { row: 0, col: 2 },
    { row: 0, col: 4 },
    { row: 1, col: 3 }
  ];

  // 白方陷阱（围绕白方巢穴）
  const whiteTraps: Position[] = [
    { row: 8, col: 2 },
    { row: 8, col: 4 },
    { row: 7, col: 3 }
  ];

  // 设置陷阱
  blackTraps.forEach(pos => {
    terrain[pos.row][pos.col] = { type: 'trap', owner: 'black' };
  });

  whiteTraps.forEach(pos => {
    terrain[pos.row][pos.col] = { type: 'trap', owner: 'white' };
  });

  // 设置巢穴（在中间位置）
  terrain[0][3] = { type: 'den', owner: 'black' };  // 黑方巢穴
  terrain[8][3] = { type: 'den', owner: 'white' };  // 白方巢穴

  return { blackTraps, whiteTraps };
};

/**
 * 创建初始游戏状态
 */
export const createInitialGameState = (
  gameMode: GameMode = 'pvp',
  difficulty?: Difficulty
): JungleGameState => {
  const board = createJungleBoard();
  const terrain = createTerrain();
  const { blackTraps, whiteTraps } = setupSpecialTerrain(terrain);

  // 获取河流位置
  const riverPositions: Position[] = [];
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 7; col++) {
      if (terrain[row][col].type === 'river') {
        riverPositions.push({ row, col });
      }
    }
  }

  // 获取双方动物
  const blackAnimals: JungleAnimal[] = [];
  const whiteAnimals: JungleAnimal[] = [];

  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 7; col++) {
      const animal = board[row][col];
      if (animal) {
        if (animal.player === 'black') {
          blackAnimals.push(animal);
        } else {
          whiteAnimals.push(animal);
        }
      }
    }
  }

  return {
    board,
    terrain,
    currentPlayer: 'black',
    status: 'playing',
    winner: null,
    moves: [],
    gameHistory: [],
    isAIThinking: false,

    // 斗兽棋特有状态
    selectedPiece: undefined,
    possibleMoves: [],

    // 地形信息
    traps: { black: blackTraps, white: whiteTraps },
    dens: { black: { row: 0, col: 3 }, white: { row: 8, col: 3 } },
    riverPositions,

    // 动物信息
    animals: { black: blackAnimals, white: whiteAnimals },
    capturedAnimals: { black: [], white: [] },

    // 游戏统计
    totalMoves: 0,
    captureMoves: 0,
    riverJumps: 0,
  };
};

// ==================== 游戏规则验证 ====================

/**
 * 检查动物是否可以吃掉另一个动物
 */
export const canCapture = (attacker: JungleAnimal, defender: JungleAnimal, defenderTerrain: JungleTerrain, attackerPosition: Position, gameState: JungleGameState): boolean => {
  // 在对方陷阱中的动物可以被任何动物吃掉
  if (defenderTerrain.type === 'trap' && defenderTerrain.owner !== defender.player) {
    return true;
  }

  // 特殊规则：鼠可以吃象，但鼠必须在陆地上才能吃陆地大象
  if (attacker.type === 'mouse' && defender.type === 'elephant') {
    const attackerTerrain = gameState.terrain[attackerPosition.row][attackerPosition.col];
    // 老鼠在水中不能吃陆地大象
    if (attackerTerrain.type === 'river' && defenderTerrain.type !== 'river') {
      return false;
    }
    return true;
  }

  // 象不能吃鼠
  if (attacker.type === 'elephant' && defender.type === 'mouse') {
    return false;
  }

  // 狮子和老虎不能吃水里的老鼠
  if ((attacker.type === 'lion' || attacker.type === 'tiger') && defender.type === 'mouse') {
    if (defenderTerrain.type === 'river') {
      return false;
    }
  }

  // 正常规则：等级高的吃等级低的或相同的
  return attacker.rank >= defender.rank;
};

/**
 * 检查是否可以进入指定地形
 */
export const canEnterTerrain = (animal: JungleAnimal, terrain: JungleTerrain, position: Position, gameState: JungleGameState): boolean => {
  // 不能进入己方巢穴
  if (terrain.type === 'den' && terrain.owner === animal.player) {
    return false;
  }

  // 只有鼠可以进入河流
  if (terrain.type === 'river' && animal.type !== 'mouse') {
    return false; // 狮子和老虎不能进入河流，只能跳过
  }

  return true;
};

/**
 * 检查狮虎是否可以跳河
 */
export const canJumpRiver = (animal: JungleAnimal, position: Position, gameState: JungleGameState): boolean => {
  if (animal.type !== 'lion' && animal.type !== 'tiger') {
    return false;
  }

  // 检查四个方向的跳河可能性
  const jumpTargets = getRiverJumpTargets(animal, position, gameState);

  return jumpTargets.length > 0;
};

/**
 * 获取所有可能的跳河目标位置
 */
export const getRiverJumpTargets = (animal: JungleAnimal, position: Position, gameState: JungleGameState): Position[] => {
  if (animal.type !== 'lion' && animal.type !== 'tiger') {
    return [];
  }

  const targets: Position[] = [];

  // 河流区域：标准斗兽棋横向河流布局
  const riverCols = [1, 2, 4, 5];  // 河流列
  const riverRows = [3, 4, 5];     // 河流行

  // 检查横向跳河（标准斗兽棋只有横向跳河）
  const directions = [
    { name: 'left', dRow: 0, dCol: -1 },   // 向左跳河
    { name: 'right', dRow: 0, dCol: 1 },    // 向右跳河
    // 竖向跳河（上下）
    { name: 'up', dRow: -1, dCol: 0 },     // 向上跳（行减小方向）
    { name: 'down', dRow: 1, dCol: 0 }     // 向下跳（行增大方向）
  ];

  for (const dir of directions) {
    const jumpTarget = checkRiverJumpInDirection(position, dir, riverCols, riverRows, gameState);
    if (jumpTarget) {
      targets.push(jumpTarget);
    }
  }

  return targets;
};

/**
 * 检查特定方向的跳河可能性
 */
const checkRiverJumpInDirection = (
  startPos: Position,
  direction: { name: string, dRow: number, dCol: number },
  riverCols: number[],
  riverRows: number[],
  gameState: JungleGameState
): Position | null => {
  let currentRow = startPos.row + direction.dRow;
  let currentCol = startPos.col + direction.dCol;

  // 检查第一步是否在河中
  if (!isValidPosition({ row: currentRow, col: currentCol })) {
    return null;
  }

  // 如果第一步不是河流，则不能跳河
  if (!riverCols.includes(currentCol) || !riverRows.includes(currentRow)) {
    return null;
  }

  // 检查河中路径上是否有老鼠阻挡
  while (isValidPosition({ row: currentRow, col: currentCol }) &&
         riverCols.includes(currentCol) && riverRows.includes(currentRow)) {

    // 检查这个河中格子是否有老鼠
    const animalInRiver = gameState.board[currentRow][currentCol];
    if (animalInRiver && animalInRiver.type === 'mouse') {
      return null; // 有老鼠阻挡，不能跳河
    }

    currentRow += direction.dRow;
    currentCol += direction.dCol;
  }

  // 检查对岸位置是否有效
  if (!isValidPosition({ row: currentRow, col: currentCol })) {
    return null;
  }

  // 对岸位置不能是河流
  if (riverCols.includes(currentCol) && riverRows.includes(currentRow)) {
    return null;
  }

  // 返回对岸位置
  return { row: currentRow, col: currentCol };
};

/**
 * 获取河对岸的位置（保留向后兼容性）
 */
export const getRiverJumpTarget = (animal: JungleAnimal, position: Position, gameState: JungleGameState): Position | null => {
  const jumpTargets = getRiverJumpTargets(animal, position, gameState);
  return jumpTargets.length > 0 ? jumpTargets[0] : null;
};

/**
 * 获取动物的可能移动
 */
export const getAnimalMoves = (
  gameState: JungleGameState,
  position: Position
): JungleMove[] => {
  const animal = gameState.board[position.row][position.col];
  if (!animal) return [];

  const moves: JungleMove[] = [];
  const terrain = gameState.terrain[position.row][position.col];

  // 检查是否可以跳河
  if (canJumpRiver(animal, position, gameState)) {
    const jumpTargets = getRiverJumpTargets(animal, position, gameState);

    for (const jumpTarget of jumpTargets) {
      const targetAnimal = gameState.board[jumpTarget.row][jumpTarget.col];
      const targetTerrain = gameState.terrain[jumpTarget.row][jumpTarget.col];

      // 检查目标位置是否可以进入
      if (!targetAnimal && canEnterTerrain(animal, targetTerrain, jumpTarget, gameState)) {
        moves.push({
          player: animal.player,
          from: position,
          to: jumpTarget,
          isRiverJump: true
        });
      } else if (targetAnimal && targetAnimal.player !== animal.player) {
        // 检查是否可以吃掉目标位置的动物（跳河可以直接吃子）
        if (canCapture(animal, targetAnimal, targetTerrain, position, gameState)) {
          moves.push({
            player: animal.player,
            from: position,
            to: jumpTarget,
            captures: [targetAnimal],
            isRiverJump: true
          });
        }
      }
    }
  }

  // 普通移动（相邻格子）
  const adjacentPositions = getAdjacentPositions(position);
  for (const adjPos of adjacentPositions) {
    const targetAnimal = gameState.board[adjPos.row][adjPos.col];
    const targetTerrain = gameState.terrain[adjPos.row][adjPos.col];

    // 检查是否可以进入该地形
    if (!canEnterTerrain(animal, targetTerrain, adjPos, gameState)) {
      continue;
    }

    // 检查目标位置
    if (!targetAnimal) {
      // 空位置，可以移动
      moves.push({
        player: animal.player,
        from: position,
        to: adjPos
      });
    } else if (targetAnimal.player !== animal.player) {
      // 敌方动物，检查是否可以吃掉
      if (canCapture(animal, targetAnimal, targetTerrain, position, gameState)) {
        moves.push({
          player: animal.player,
          from: position,
          to: adjPos,
          captures: [targetAnimal]
        });
      }
    }
  }

  return moves;
};

/**
 * 获取玩家的所有可能移动
 */
export const getAllPossibleMoves = (
  gameState: JungleGameState,
  player: Player
): JungleMove[] => {
  const allMoves: JungleMove[] = [];

  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 7; col++) {
      const animal = gameState.board[row][col];
      if (animal && animal.player === player) {
        const position = { row, col };
        const moves = getAnimalMoves(gameState, position);
        allMoves.push(...moves);
      }
    }
  }

  return allMoves;
};

// ==================== 游戏操作 ====================

/**
 * 执行移动
 */
export const executeMove = (
  gameState: JungleGameState,
  move: JungleMove
): JungleGameState => {
  const newBoard = gameState.board.map(row => [...row]);
  const newTerrain = gameState.terrain.map(row => [...row]);
  const animal = newBoard[move.from.row][move.from.col]!;

  // 移动动物
  newBoard[move.to.row][move.to.col] = animal;
  newBoard[move.from.row][move.from.col] = null;

  // 处理吃子
  const newCapturedAnimals = { ...gameState.capturedAnimals };
  if (move.captures && move.captures.length > 0) {
    const capturedAnimal = move.captures[0];
    newCapturedAnimals[animal.player] = [...newCapturedAnimals[animal.player], capturedAnimal];
  }

  // 更新动物列表
  const newAnimals = { black: [...gameState.animals.black], white: [...gameState.animals.white] };
  if (move.captures && move.captures.length > 0) {
    const capturedAnimal = move.captures[0];
    const opponentPlayer = capturedAnimal.player;
    newAnimals[opponentPlayer] = newAnimals[opponentPlayer].filter(a =>
      !(a.type === capturedAnimal.type && a.player === capturedAnimal.player)
    );
  }

  // 创建新游戏状态
  const newGameState: JungleGameState = {
    ...gameState,
    board: newBoard,
    terrain: newTerrain,
    currentPlayer: gameState.currentPlayer === 'black' ? 'white' : 'black',
    moves: [...gameState.moves, move],
    gameHistory: [...gameState.gameHistory, gameState],
    animals: newAnimals,
    capturedAnimals: newCapturedAnimals,

    // 更新统计
    totalMoves: gameState.totalMoves + 1,
    captureMoves: gameState.captureMoves + (move.captures?.length || 0),
    riverJumps: gameState.riverJumps + (move.isRiverJump ? 1 : 0),

    // 重置选择状态
    selectedPiece: undefined,
    possibleMoves: [],
  };

  return newGameState;
};

/**
 * 检查游戏是否结束
 */
export const checkGameEnd = (
  gameState: JungleGameState
): { isEnd: boolean; winner: Player | null; reason: string } => {
  // 检查是否占领对方巢穴
  const blackDen = gameState.dens.black;
  const whiteDen = gameState.dens.white;

  const blackDenAnimal = gameState.board[blackDen.row][blackDen.col];
  const whiteDenAnimal = gameState.board[whiteDen.row][whiteDen.col];

  // 白方占领黑方巢穴
  if (blackDenAnimal && blackDenAnimal.player === 'white') {
    return { isEnd: true, winner: 'white', reason: '白方占领黑方巢穴！' };
  }

  // 黑方占领白方巢穴
  if (whiteDenAnimal && whiteDenAnimal.player === 'black') {
    return { isEnd: true, winner: 'black', reason: '黑方占领白方巢穴！' };
  }

  // 检查是否有一方所有动物被吃掉
  if (gameState.animals.black.length === 0) {
    return { isEnd: true, winner: 'white', reason: '黑方所有动物被吃掉！' };
  }

  if (gameState.animals.white.length === 0) {
    return { isEnd: true, winner: 'black', reason: '白方所有动物被吃掉！' };
  }

  // 检查当前玩家是否有可用移动
  const possibleMoves = getAllPossibleMoves(gameState, gameState.currentPlayer);
  if (possibleMoves.length === 0) {
    const winner = gameState.currentPlayer === 'black' ? 'white' : 'black';
    return { isEnd: true, winner, reason: `${gameState.currentPlayer === 'black' ? '黑方' : '白方'}无法移动！` };
  }

  return { isEnd: false, winner: null, reason: '' };
};

// ==================== 游戏状态管理 ====================

/**
 * 选择动物
 */
export const selectAnimal = (
  gameState: JungleGameState,
  position: Position
): JungleGameState => {
  const animal = gameState.board[position.row][position.col];

  if (!animal || animal.player !== gameState.currentPlayer) {
    return {
      ...gameState,
      selectedPiece: undefined,
      possibleMoves: []
    };
  }

  // 获取可能的移动
  const possibleMoves = getAnimalMoves(gameState, position);

  return {
    ...gameState,
    selectedPiece: position,
    possibleMoves
  };
};

/**
 * 取消选择动物
 */
export const deselectAnimal = (gameState: JungleGameState): JungleGameState => {
  return {
    ...gameState,
    selectedPiece: undefined,
    possibleMoves: []
  };
};

/**
 * 执行移动
 */
export const makeMove = (
  gameState: JungleGameState,
  move: JungleMove
): JungleGameState => {
  // 执行移动
  const newGameState = executeMove(gameState, move);

  // 检查游戏是否结束
  const gameEnd = checkGameEnd(newGameState);
  if (gameEnd.isEnd) {
    return {
      ...newGameState,
      status: 'finished',
      winner: gameEnd.winner
    };
  }

  return newGameState;
};

export default {
  // 基础函数
  isValidPosition,
  getAdjacentPositions,

  // 棋盘和地形
  createJungleBoard,
  createTerrain,
  setupSpecialTerrain,
  createInitialGameState,

  // 游戏规则
  canCapture,
  canEnterTerrain,
  canJumpRiver,
  getRiverJumpTarget,
  getAnimalMoves,
  getAllPossibleMoves,
  executeMove,
  checkGameEnd,

  // 游戏状态管理
  selectAnimal,
  deselectAnimal,
  makeMove
};